
import { defineComponent } from '@nuxtjs/composition-api';
import type { PropType } from 'vue';
import type { BravadoSelectDropdownOption } from '../BravadoSelectDropdown/types';
import BravadoSelectDropdown from '../BravadoSelectDropdown/BravadoSelectDropdown.vue';
import BravadoSelectDropdownHandlerMixin from '../BravadoSelectDropdown/BravadoSelectDropdownHandler.mixin';
import BravadoComboBoxItem from './BravadoComboBoxItem.vue';
import { guid } from '~/components/utils/core/uid';

export default defineComponent({
  name: 'BravadoComboBox',
  components: {
    BravadoSelectDropdown,
    BravadoComboBoxItem,
  },
  mixins: [BravadoSelectDropdownHandlerMixin],
  props: {
    term: {
      type: String,
      required: true,
    },
    isCreatable: {
      type: Boolean,
      default: false,
    },
    isTextArea: {
      type: Boolean,
      default: false,
    },
    minLength: {
      type: Number,
      default: 2,
    },
    dropdownPosition: {
      type: String as PropType<'down' | 'up'>,
      default: 'down',
    },
    actionKeyUp: {
      type: String as PropType<'none' | 'enter' | 'space'>,
      default: 'none',
    },
  },
  computed: {
    selectDropdownProps() {
      return {
        args: {
          options: this.sortedOptions,
          selectedOptions: this.value,
          showOptions: this.showOptions,
          selectId: this.id,
          isCreatable: this.isCreatable,
          term: this.term,
          minLength: this.minLength,
          position: this.dropdownPosition,
          getIsRemovable: this.getIsRemovable,
        },
        listeners: {
          'toggle-option': this.handleToggleOptionWrapper,
          'close-options': this.handleCloseOptions,
          'create-option': () => this.handleCreateOption(this.term),
        },
      };
    },
    selectedOptions() {
      const nonRemovableOptions: Array<BravadoSelectDropdownOption> = [];
      const removableOptions: Array<BravadoSelectDropdownOption> = [];

      this.value.forEach(selectedOption => {
        const arrayToPush = this.getIsRemovable(selectedOption) ? removableOptions : nonRemovableOptions;
        arrayToPush.push(selectedOption);
      });

      return {
        nonRemovableOptions,
        removableOptions,
      };
    },
    togglerClasses() {
      return {
        'bravado-combo-box__toggler': true,
        'bravado-combo-box__toggler--error': this.isInvalid,
        'bravado-combo-box__toggler--disabled': this.isDisabled,
        'bravado-combo-box__toggler--text-area': this.isTextArea,
      };
    },
  },
  methods: {
    focusInput() {
      if (!this.$refs.input) return;

      (this.$refs.input as HTMLInputElement).focus();
    },
    openOptions(term: string) {
      if (!this.multi && this.value[0] && this.getIsRemovable(this.value[0])) return;
      if (term.length < this.minLength) return;
      this.showOptions = true;
    },
    onClickContainer() {
      this.focusInput();
      this.openOptions(this.term);
    },
    reset() {
      this.$emit('search', '');
      this.handleCloseOptions();
    },
    handleUpdateTerm(event: InputEvent) {
      const value = (event.target as HTMLInputElement).value;
      this.$emit('search', value);
      this.openOptions(value);
    },
    handleRemoveOption(option: BravadoSelectDropdownOption) {
      const newValue = this.value.filter(
        selectedOption => selectedOption.selectDropdownValue !== option.selectDropdownValue,
      );
      this.$emit('input', newValue);
    },
    handleActionKeyUp(event: KeyboardEvent) {
      const isActionKey =
        this.actionKeyUp !== 'none' &&
        ((event.key === 'Enter' && this.actionKeyUp === 'enter') ||
          (event.key === ' ' && this.actionKeyUp === 'space'));

      if (!isActionKey || !this.isCreatable) {
        return;
      }

      this.handleCreateOption(this.term.trim());
    },
    handleCreateOption(term: string) {
      this.$emit('create-option', {
        selectDropdownTitle: term,
        selectDropdownValue: guid(),
      } as BravadoSelectDropdownOption);
      this.reset();
    },
    handleToggleOptionWrapper(option: BravadoSelectDropdownOption) {
      this.reset();
      this.handleToggleOption(option);
    },
  },
});
